import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { LogOut, Shield } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { isInTenantSupportMode } from '@/auth/roles'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'

/**
 * Sticky banner shown while a super admin is operating inside a tenant.
 * One-click exit restores platform-only access.
 */
export function SupportModeBanner() {
  const { user, exitTenant } = useAuth()
  const navigate = useNavigate()
  const [error, setError] = React.useState<string | null>(null)
  const [exiting, setExiting] = React.useState(false)

  if (!user || !isInTenantSupportMode(user)) {
    return null
  }

  const tenantLabel = user.support.tenantName

  async function handleExit(): Promise<void> {
    setError(null)
    setExiting(true)
    try {
      await exitTenant()
      navigate('/tenants', { replace: true })
    } catch (e) {
      setError(requestErrorMessage(e))
      setExiting(false)
    }
  }

  return (
    <div
      role="status"
      className="flex flex-wrap items-center justify-between gap-3 border-b border-hold-ink/40 bg-hold-soft px-4 py-2 text-sm text-hold-ink"
    >
      <div className="flex min-w-0 items-center gap-2">
        <Shield className="h-4 w-4 shrink-0" aria-hidden />
        <p className="min-w-0">
          You&apos;re in <span className="font-medium">{tenantLabel}</span> as super admin.
          Changes you make apply to this organization.
        </p>
      </div>
      <div className="flex items-center gap-2">
        {error ? <span className="text-xs text-danger-ink">{error}</span> : null}
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={exiting}
          onClick={() => void handleExit()}
          className="border-hold-ink/50 bg-surface/80 hover:bg-surface"
        >
          <LogOut className="h-3.5 w-3.5" />
          {exiting ? 'Exiting…' : 'Exit tenant'}
        </Button>
      </div>
    </div>
  )
}
